import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Genre mapping from TMDB
const genreMap = {
  28: 'Action',
  12: 'Adventure',
  16: 'Animation',
  35: 'Comedy',
  80: 'Crime',
  99: 'Documentary',
  18: 'Drama',
  10751: 'Family',
  14: 'Fantasy',
  36: 'History',
  27: 'Horror',
  10402: 'Music',
  9648: 'Mystery',
  10749: 'Romance',
  878: 'Sci-Fi',
  10770: 'TV Movie',
  53: 'Thriller',
  10752: 'War',
  37: 'Western'
};

// Convert genre IDs to names
export const getGenreNames = (genreIds) => {
  return genreIds.map(id => genreMap[id] || 'Unknown').filter(Boolean);
};

// Movies API
export const moviesApi = {
  getPopular: async (page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/movies/popular`, {
      params: { page, language }
    });
    return response.data;
  },

  getTopRated: async (page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/movies/top-rated`, {
      params: { page, language }
    });
    return response.data;
  },

  getNowPlaying: async (page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/movies/now-playing`, {
      params: { page, language }
    });
    return response.data;
  },

  getUpcoming: async (page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/movies/upcoming`, {
      params: { page, language }
    });
    return response.data;
  },

  getDocumentaries: async (page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/movies/documentaries`, {
      params: { page, language }
    });
    return response.data;
  },

  getCultFilms: async (page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/movies/cult`, {
      params: { page, language }
    });
    return response.data;
  },

  search: async (query, page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/movies/search`, {
      params: { query, page, language }
    });
    return response.data;
  },

  searchAll: async (query, page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/search/movies`, {
      params: { query, page, language }
    });
    return response.data;
  },

  getDetail: async (movieId, language = 'en-US') => {
    const response = await axios.get(`${API}/movies/${movieId}`, {
      params: { language }
    });
    return response.data;
  },

  getSimilar: async (movieId, page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/movies/${movieId}/similar`, {
      params: { page, language }
    });
    return response.data;
  },

  getGenres: async (language = 'en-US') => {
    const response = await axios.get(`${API}/genres`, {
      params: { language }
    });
    return response.data;
  }
};

// People/Stars API
export const peopleApi = {
  getPopular: async (page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/people/popular`, {
      params: { page, language }
    });
    return response.data;
  },

  search: async (query, page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/people/search`, {
      params: { query, page, language }
    });
    return response.data;
  },

  getDetail: async (personId, language = 'en-US') => {
    const response = await axios.get(`${API}/people/${personId}`, {
      params: { language }
    });
    return response.data;
  }
};

// TV Series API
export const tvApi = {
  getPopular: async (page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/tv/popular`, {
      params: { page, language }
    });
    return response.data;
  },

  getTrending: async (timeWindow = 'week', page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/tv/trending`, {
      params: { time_window: timeWindow, page, language }
    });
    return response.data;
  },

  getTopRated: async (page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/tv/top-rated`, {
      params: { page, language }
    });
    return response.data;
  },

  getOnTheAir: async (page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/tv/on-the-air`, {
      params: { page, language }
    });
    return response.data;
  },

  search: async (query, page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/tv/search`, {
      params: { query, page, language }
    });
    return response.data;
  },

  getDetail: async (tvId, language = 'en-US') => {
    const response = await axios.get(`${API}/tv/${tvId}`, {
      params: { language }
    });
    return response.data;
  },

  getSimilar: async (tvId, page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/tv/${tvId}/similar`, {
      params: { page, language }
    });
    return response.data;
  },

  getGenres: async (language = 'en-US') => {
    const response = await axios.get(`${API}/tv/genres`, {
      params: { language }
    });
    return response.data;
  },

  getProviders: async (language = 'en-US') => {
    const response = await axios.get(`${API}/tv/providers`, {
      params: { language }
    });
    return response.data;
  },

  getByNetwork: async (networkId, page = 1, language = 'en-US') => {
    const response = await axios.get(`${API}/tv/by-network/${networkId}`, {
      params: { page, language }
    });
    return response.data;
  }
};

// Reviews API
export const reviewsApi = {
  getFeatured: async (limit = 8) => {
    const response = await axios.get(`${API}/reviews/featured`, {
      params: { limit }
    });
    return response.data;
  },

  getAll: async (options = {}) => {
    const { category, search, genre, sort_by, limit = 50 } = options;
    const params = { limit };
    if (category && category !== 'all') params.category = category;
    if (search) params.search = search;
    if (genre && genre !== 'all') params.genre = genre;
    if (sort_by) params.sort_by = sort_by;
    const response = await axios.get(`${API}/reviews`, { params });
    return response.data;
  },

  getByMovie: async (tmdbId) => {
    const response = await axios.get(`${API}/reviews/by-movie/${tmdbId}`);
    return response.data;
  },

  getById: async (reviewId) => {
    const response = await axios.get(`${API}/reviews/${reviewId}`);
    return response.data;
  },

  create: async (reviewData) => {
    const response = await axios.post(`${API}/reviews`, reviewData);
    return response.data;
  },

  update: async (reviewId, reviewData) => {
    const response = await axios.put(`${API}/reviews/${reviewId}`, reviewData);
    return response.data;
  },

  delete: async (reviewId) => {
    const response = await axios.delete(`${API}/reviews/${reviewId}`);
    return response.data;
  }
};

// Language mapping for TMDB
export const languageToTMDB = {
  en: 'en-US',
  es: 'es-ES',
  pt: 'pt-BR',
  fr: 'fr-FR',
  de: 'de-DE',
  it: 'it-IT'
};
